import React from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faUpload, faImage } from '@fortawesome/free-solid-svg-icons';
import { faAmazon, faGoogle } from '@fortawesome/free-brands-svg-icons';

/**
 * Configuration for image source badge display.
 * Maps source type to visual properties.
 */
const SOURCE_CONFIG = {
  brand: {
    label: 'Brand',
    icon: faGlobe,
    variant: 'primary',
    tooltip: 'Image sourced from brand manufacturer website'
  },
  amazon: {
    label: 'Amazon',
    icon: faAmazon,
    variant: 'warning',
    tooltip: 'Image sourced from Amazon product listing'
  },
  google: {
    label: 'Google',
    icon: faGoogle,
    variant: 'info',
    tooltip: 'Image sourced from Google Images search'
  },
  manual: {
    label: 'Manual',
    icon: faUpload,
    variant: 'secondary',
    tooltip: 'Image manually uploaded'
  },
  existing: {
    label: 'Existing',
    icon: faImage,
    variant: 'success',
    tooltip: 'Existing product image'
  }
};

/**
 * ImageSourceBadge - Shows source indicator for product images.
 *
 * Features:
 * - Visual badge indicating image source (brand/Amazon/Google/manual)
 * - Tooltip with source description
 * - Configurable size (compact vs normal)
 *
 * @param {string} source - Source type: 'brand', 'amazon', 'google', 'manual', 'existing'
 * @param {boolean} compact - Show compact single-letter badge (default: false)
 * @param {string} className - Additional CSS classes
 */
const ImageSourceBadge = ({ source, compact = false, className = '' }) => {
  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.existing;

  const badge = (
    <Badge
      bg={config.variant}
      className={`image-source-badge ${compact ? 'badge-compact' : ''} ${className}`}
      style={compact ? { fontSize: '0.65rem', padding: '0.15rem 0.35rem' } : {}}
    >
      <FontAwesomeIcon icon={config.icon} className={compact ? '' : 'me-1'} />
      {!compact && config.label}
    </Badge>
  );

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`source-tooltip-${source}`}>
          {config.tooltip}
        </Tooltip>
      }
    >
      {badge}
    </OverlayTrigger>
  );
};

/**
 * ImageSourceBadgeList - Renders multiple source badges for a product.
 *
 * @param {string[]} sources - Array of source types
 * @param {boolean} compact - Show compact badges
 */
const ImageSourceBadgeList = ({ sources = [], compact = false }) => {
  if (!sources || sources.length === 0) return null;

  // Deduplicate and prioritize
  const uniqueSources = [...new Set(sources)];
  const priorityOrder = ['brand', 'amazon', 'google', 'manual', 'existing'];
  const sortedSources = uniqueSources.sort(
    (a, b) => priorityOrder.indexOf(a) - priorityOrder.indexOf(b)
  );

  return (
    <div className="d-flex flex-wrap gap-1">
      {sortedSources.map(source => (
        <ImageSourceBadge key={source} source={source} compact={compact} />
      ))}
    </div>
  );
};

export { ImageSourceBadge, ImageSourceBadgeList, SOURCE_CONFIG };
export default ImageSourceBadge;
